import React from "react";
import { Form, Field } from "react-final-form";
import { FORM_ERROR } from "final-form";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { httpClient } from "common/httpClient";
import * as icons from "common/icons";
import { useRefData, useModal } from "common/hooks";
import * as utils from "common/utils";
import { useAnalysis } from "containers/hooks";
import { Modal, ModalBody, ModalFooter, ModalFooterLeft, ModalFooterRight } from "components/Modal";
import { Grid, GridCell } from "components/Grid";
import { SortableTable, SortableTableHeader } from "components/SortableTable";
import { SelectField, TagSelectorField } from "components/FormFields";
import { Button } from "components/Button";

export default function TagBlocksModal({ publication, close }) {
    const { data: publishedAnalysis } = useAnalysis(publication.farmId, publication.publishedAnalysisId);
    const { viewModel, availableTags, handleTagAdded } = useViewModelAndAvailableTags(publishedAnalysis);
    const updatePublicationBlockTagsAsync = useUpdatePublicationBlockTagsAsync(publication.id);

    const submit = async (viewModel) => {
        const publicationBlockTags = {
            publicationId: publication.id,
            blockTags: Object.keys(viewModel.indexedByBlock).map((blockId) => ({
                blockId,
                tags: viewModel.indexedByBlock[blockId].tags,
            })),
        }
        await updatePublicationBlockTagsAsync(publicationBlockTags)
            .then(close)
            .catch((ex) => ({ [FORM_ERROR]: ex.message }));
    };

    const info = "Block tags are used to filter blocks by tag. Select an existing tag from the list or begin typing in the list to add a new tag.";

    return (
        <Form initialValues={viewModel} onSubmit={submit}>
            {({ values, handleSubmit, submitting, submitError, dirtySinceLastSubmit }) => {
                const { indexedByBlock = [], fetchError } = values;
                const blocks = Object.values(indexedByBlock).map((block) => ({ ...block, tags: block.tags || [] }));
                const error = fetchError || (!dirtySinceLastSubmit && submitError);

                const filteredBlocks = [];
                for (const block of blocks) {
                    const blockIsIncludedInFilter = !values.filterTags || (values.filterTags === "UNTAGGED_BLOCKS" && block.tags.length === 0) || block.tags?.includes(values.filterTags);
                    if (blockIsIncludedInFilter) {
                        filteredBlocks.push(block);
                    }
                }

                const options = availableTags.map((tag) => ({ value: tag, text: tag, groupLabel: "Tags", groupIndex: -1 }));
                options.unshift({ value: "UNTAGGED_BLOCKS", text: "Untagged blocks" });

                return (
                    <form onSubmit={handleSubmit}>
                        <Modal title="Block tags" close={close} submitting={submitting} wide>
                            <ModalBody loading={!filteredBlocks} info={info} error={error}>
                                <Grid className="u-pt-lg">
                                    <GridCell className="u-width1of4">
                                        <label className="Field-label u-pl-sm">Tag filter</label>
                                        <div className="Widget-header-select">
                                            <Field name="filterTags" placeholder="All blocks" options={options} component={SelectField} />
                                        </div>
                                    </GridCell>
                                </Grid>
                                {filteredBlocks?.length > 0 && (
                                    <>
                                        {filteredBlocks?.length > 0 ? (
                                            <div className="u-pt-lg">
                                                <SortableTable data={filteredBlocks} defaultSortCriteria={{ fieldName: "name", fieldType: "string" }} pageSize={50}>
                                                    {({ data, sortCriteria, handleSort }) => {
                                                        return (
                                                            data.length > 0 && (
                                                                <table>
                                                                    <thead>
                                                                        <tr>
                                                                            <SortableTableHeader label="Block" fieldName="name" sortCriteria={sortCriteria} handleSort={handleSort} />
                                                                            <th data-width="50">Tags</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {data.map((block) => {
                                                                            return (
                                                                                <tr key={block.id} valign="top">
                                                                                    <td className="u-pt-lg">
                                                                                        <div style={{ whiteSpace: "pre-wrap" }} dangerouslySetInnerHTML={{ __html: `<b>${block.name}</b><br />${block.description}` }} />
                                                                                    </td>
                                                                                    <td>
                                                                                        <Field name={`indexedByBlock[${block.id}].tags`} availableTags={availableTags} onTagAdded={handleTagAdded} component={TagSelectorField} />
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                    </tbody>
                                                                </table>
                                                            )
                                                        );
                                                    }}
                                                </SortableTable>
                                            </div>
                                        ) : (
                                            <div className="u-mt-lg u-mb-lg">
                                                <div className="Tile-body">
                                                    <div className="Tile-body-message">
                                                        <img className="u-p-5" src={icons.info} alt="Plus" />
                                                        <p className="lead">There are no blocks for this filter</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </ModalBody>
                            <ModalFooter>
                                <ModalFooterLeft>
                                    <Button id="cancel" onClick={close} secondary disabled={submitting}>
                                        Cancel
                                    </Button>
                                </ModalFooterLeft>
                                <ModalFooterRight>
                                    <Button id="submit" submit primary disabled={fetchError || submitting}>
                                        Save
                                    </Button>
                                </ModalFooterRight>
                            </ModalFooter>
                        </Modal>
                    </form>
                );
            }}
        </Form>
    );
}

function usePublicationBlockTags(publicationId) {
    const query = useQuery({
        queryKey: ["publications", publicationId, "blockTags"],
        queryFn: async () => httpClient.get(`publications/${publicationId}/blockTags`),
        retry: false,
        refetchOnWindowFocus: false,
        enabled: !!publicationId,
    });

    return {
        isFetching: query.isFetching,
        isLoading: query.isLoading,
        data: query.data,
        error: query.error,
    };
}

function useViewModelAndAvailableTags(publishedAnalysis) {
    const refData = useRefData();
    const { data: publicationBlockTags } = usePublicationBlockTags(publishedAnalysis?.publicationId);
    const [viewModel, setViewModel] = React.useState();
    const [availableTags, setAvailableTags] = React.useState([]);

    React.useEffect(() => {
        let isMounted = true;
        const fetchData = async (publishedAnalysis) => {
            try {
                const blocks = publishedAnalysis?.blocks?.map((block) => {
                    const tags = publicationBlockTags?.find((blockTag) => blockTag.blockId === block.id)?.tags || [];
                    const description = utils.getBlockTypeText(refData, block);
                    return {
                        id: block.id,
                        name: block.name,
                        description,
                        tags
                    };
                }) || [];
                const availableTags = blocks.reduce((distinctBlockTags, block) => {
                    const newBlockTags = block.tags.filter((blockTag) => !distinctBlockTags.includes(blockTag));
                    return distinctBlockTags.concat(newBlockTags);
                }, []);
                const indexedByBlock = blocks.reduce((indexed, block) => {
                    indexed[block.id] = block;
                    return indexed;
                }, {});
                if (isMounted) {
                    setViewModel({ indexedByBlock });
                    setAvailableTags(availableTags);
                }
            } catch (fetchError) {
                if (isMounted) setViewModel({ fetchError });
            }
        };
        if (publishedAnalysis) {
            fetchData(publishedAnalysis);
        }
        return () => (isMounted = false);
    }, [publishedAnalysis, publicationBlockTags, refData]);

    const handleTagAdded = (newTag) => {
        if (newTag) setAvailableTags([...availableTags, newTag]);
    };

    return { viewModel, availableTags, handleTagAdded };
}

function useUpdatePublicationBlockTagsAsync(publicationId) {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: async (publicationBlockTags) => {
            try {
                await httpClient.put(`publications/${publicationId}/blockTags`, publicationBlockTags);
            } catch (error) {
                if (error.status === 401 || error.status === 403) {
                    throw new Error("You are not authorised to make this change.");
                } else {
                    throw new Error(error.message);
                }
            }
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ["publications", variables.id, "blockTags"] });
        },
    });

    return (publicationBlockTags) => mutation.mutateAsync(publicationBlockTags);
}

export function useTagBlocksModal(publication) {
    const [modal, openModal] = useModal(TagBlocksModal);

    const open = () => {
        const modalProps = {
            publication,
        };
        openModal(modalProps);
    };

    return [modal, open];
}
